import { Field, ID, ObjectType } from 'type-graphql'
import {
  Column,
  CreateDateColumn,
  Entity,
  Index,
  JoinColumn,
  ManyToOne,
  OneToOne,
  PrimaryGeneratedColumn,
  RelationId,
  UpdateDateColumn
} from 'typeorm'

import { User } from '@things-factory/auth-base'
import { config } from '@things-factory/env'
import { Domain, ScalarObject, json5Transformer } from '@things-factory/shell'
import { ActivityInstance } from '@things-factory/worklist'

import { DataItem } from '../data-set/data-item-type.js'
import { DataSet } from '../data-set/data-set.js'
import { DataOoc } from '../data-ooc/data-ooc.js'

const ORMCONFIG = config.get('ormconfig', {})
const DATABASE_TYPE = ORMCONFIG.type
@Entity()
@Index(
  'ix_data_sample_0',
  (dataSample: DataSample) => [
    dataSample.domain,
    dataSample.dataSet,
    dataSample.workDate,
    dataSample.workShift,
    dataSample.collectedAt
  ],
  {
    unique: false
  }
)
@Index(
  'ix_data_sample_1',
  (dataSample: DataSample) => [dataSample.domain, dataSample.dataSet, dataSample.collectedAt],
  { unique: false }
)
@ObjectType({
  description:
    'Represents a single collected data sample, including values, metadata, judgment results, and links to the dataset and workflow activities.'
})
export class DataSample {
  @PrimaryGeneratedColumn('uuid')
  @Field(type => ID, { description: 'Unique identifier for the data sample.' })
  readonly id: string

  @ManyToOne(type => Domain)
  @Field(type => Domain)
  domain?: Domain

  @RelationId((dataSample: DataSample) => dataSample.domain)
  domainId?: string

  @Column()
  @Field({ nullable: true })
  name: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  description?: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  type?: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  useCase?: string

  @Column('simple-json', { nullable: true })
  @Field(type => ScalarObject, { nullable: true })
  partitionKeys?: { [key: string]: any }

  @ManyToOne(type => DataSet)
  @Field(type => DataSet, { nullable: false })
  dataSet?: DataSet

  @RelationId((dataSample: DataSample) => dataSample.dataSet)
  dataSetId?: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  dataSetVersion?: number

  @Column('simple-json', { nullable: true })
  @Field(type => ScalarObject, { nullable: true, description: 'The actual collected data values for this sample.' })
  data?: any

  @Column('simple-json', { nullable: true })
  @Field(type => ScalarObject, {
    nullable: true,
    description:
      'Quality judgment results for this sample, including pass/fail status and any automated analysis results.'
  })
  judgment?: any

  @Field(type => [DataItem], { nullable: true })
  dataItems?: DataItem[]

  @Column({
    nullable: true,
    type:
      DATABASE_TYPE == 'mysql' || DATABASE_TYPE == 'mariadb'
        ? 'longtext'
        : DATABASE_TYPE == 'oracle'
          ? 'clob'
          : DATABASE_TYPE == 'mssql'
            ? 'nvarchar'
            : 'varchar',
    length: DATABASE_TYPE == 'mssql' ? 'MAX' : undefined,
    transformer: json5Transformer
  })
  @Field({ nullable: true })
  rawData?: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  source?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Indicates if this sample is out of control (OOC).' })
  ooc?: boolean

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Indicates if this sample is out of specification (OOS).' })
  oos?: boolean

  @Column({ nullable: true, default: '' })
  @Field({ nullable: true })
  key01?: string = ''

  @Column({ nullable: true, default: '' })
  @Field({ nullable: true })
  key02?: string = ''

  @Column({ nullable: true, default: '' })
  @Field({ nullable: true })
  key03?: string = ''

  @Column({ nullable: true, default: '' })
  @Field({ nullable: true })
  key04?: string = ''

  @Column({ nullable: true, default: '' })
  @Field({ nullable: true })
  key05?: string = ''

  @Column({ nullable: true })
  @Field({ nullable: true })
  workDate?: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  workShift?: string

  @OneToOne(type => ActivityInstance, { nullable: true })
  @JoinColumn()
  @Field({ nullable: true })
  reviewActivityInstance?: ActivityInstance

  @RelationId((dataSample: DataSample) => dataSample.reviewActivityInstance)
  reviewActivityInstanceId?: string

  @OneToOne(type => DataOoc, dataOoc => dataOoc.dataSample, { nullable: true })
  @Field(type => DataOoc, { nullable: true })
  dataOoc?: DataOoc

  @Column({ nullable: true })
  @Field({ nullable: true })
  collectedAt?: Date

  @CreateDateColumn()
  @Field({ nullable: true })
  createdAt?: Date

  @UpdateDateColumn()
  @Field({ nullable: true })
  updatedAt?: Date

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true })
  creator?: User

  @RelationId((dataSample: DataSample) => dataSample.creator)
  creatorId?: string

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true })
  updater?: User

  @RelationId((dataSample: DataSample) => dataSample.updater)
  updaterId?: string
}
